// [PURPOSE]: Single source of truth for all custom-feat author vocabulary
//   (prereq option lists, choice presets, effect attribute dropdowns, etc.).
// [WHY]: Per Rule 22 (no inline test fabrications) and to prevent FeatsPanel ↔
//   CustomFeatCreator vocabulary drift when both components consume the same
//   dropdown sources. Pre-extraction, these constants lived inside
//   FeatsPanel.js (top-level + inside the component). The new CustomFeatCreator
//   (Stone 6 closure, 2026-05-18) imports from here; FeatsPanel.js also
//   imports from here so the two stay in lockstep.
// [INTEGRATION]: Consumed by:
//   - src/components/CustomFeatCreator.js (new)
//   - src/components/FeatsPanel.js (inline creator — to be deprecated in v1.x)
//
// FIGHTING_STYLES is imported lazily inside the CUSTOM_CHOICES_PRESETS preset
// generator to avoid an unused-import warning when only PREREQ_* is consumed.

import { FIGHTING_STYLES } from './srdCharacterData';

// ============================================================================
// PREREQUISITE PROFICIENCY PICKER LISTS (P2.1 — APR 2026)
// ============================================================================
// [SHAPE]: Each entry is { id, label }. The id is the runtime key the
//   prerequisite-check uses; the label is the display string. Saved on the
//   feat as the id (string) — the prerequisites array remains plain strings.
// [WHY]: Pre-fix the Required Proficiencies field was a free-text input.
//   Free-text proficiency strings can't drive the registry — runtime matches
//   by id (skill keys like 'stealth', save keys like 'strSave'), not by name.
//   The picker constrains authoring to canonical ids while preserving legacy
//   free-text values as chips for round-trip.

export const PREREQ_SKILLS = [
  { id: 'skill:acrobatics', label: 'Acrobatics' },
  { id: 'skill:animalHandling', label: 'Animal Handling' },
  { id: 'skill:arcana', label: 'Arcana' },
  { id: 'skill:athletics', label: 'Athletics' },
  { id: 'skill:deception', label: 'Deception' },
  { id: 'skill:history', label: 'History' },
  { id: 'skill:insight', label: 'Insight' },
  { id: 'skill:intimidation', label: 'Intimidation' },
  { id: 'skill:investigation', label: 'Investigation' },
  { id: 'skill:medicine', label: 'Medicine' },
  { id: 'skill:nature', label: 'Nature' },
  { id: 'skill:perception', label: 'Perception' },
  { id: 'skill:performance', label: 'Performance' },
  { id: 'skill:persuasion', label: 'Persuasion' },
  { id: 'skill:religion', label: 'Religion' },
  { id: 'skill:sleightOfHand', label: 'Sleight of Hand' },
  { id: 'skill:stealth', label: 'Stealth' },
  { id: 'skill:survival', label: 'Survival' },
];

export const PREREQ_TOOLS = [
  { id: "tool:smith's tools", label: "Smith's Tools" },
  { id: "tool:thieves' tools", label: "Thieves' Tools" },
  { id: 'tool:herbalism kit', label: 'Herbalism Kit' },
  { id: "tool:tinker's tools", label: "Tinker's Tools" },
  { id: "tool:carpenter's tools", label: "Carpenter's Tools" },
  { id: "tool:mason's tools", label: "Mason's Tools" },
  { id: "tool:cook's utensils", label: "Cook's Utensils" },
  { id: "tool:jeweler's tools", label: "Jeweler's Tools" },
  { id: "tool:leatherworker's tools", label: "Leatherworker's Tools" },
  { id: "tool:navigator's tools", label: "Navigator's Tools" },
  { id: "tool:painter's supplies", label: "Painter's Supplies" },
  { id: "tool:alchemist's supplies", label: "Alchemist's Supplies" },
  { id: "tool:brewer's supplies", label: "Brewer's Supplies" },
  { id: "tool:calligrapher's supplies", label: "Calligrapher's Supplies" },
  { id: "tool:cartographer's tools", label: "Cartographer's Tools" },
  { id: "tool:cobbler's tools", label: "Cobbler's Tools" },
  { id: "tool:glassblower's tools", label: "Glassblower's Tools" },
  { id: "tool:potter's tools", label: "Potter's Tools" },
  { id: "tool:weaver's tools", label: "Weaver's Tools" },
  { id: "tool:woodcarver's tools", label: "Woodcarver's Tools" },
  { id: "tool:poisoner's kit", label: "Poisoner's Kit" },
];

export const PREREQ_ARMOR = [
  { id: 'armor:light', label: 'Light Armor' },
  { id: 'armor:medium', label: 'Medium Armor' },
  { id: 'armor:heavy', label: 'Heavy Armor' },
  { id: 'armor:shield', label: 'Shields' },
];

export const PREREQ_WEAPONS = [
  { id: 'weapon:simple', label: 'Simple Weapons' },
  { id: 'weapon:martial', label: 'Martial Weapons' },
];

export const PREREQ_SAVES = [
  { id: 'save:str', label: 'Strength Save' },
  { id: 'save:dex', label: 'Dexterity Save' },
  { id: 'save:con', label: 'Constitution Save' },
  { id: 'save:int', label: 'Intelligence Save' },
  { id: 'save:wis', label: 'Wisdom Save' },
  { id: 'save:cha', label: 'Charisma Save' },
];

export const PREREQ_LANGUAGES = [
  { id: 'language:common', label: 'Common' },
  { id: 'language:dwarvish', label: 'Dwarvish' },
  { id: 'language:elvish', label: 'Elvish' },
  { id: 'language:giant', label: 'Giant' },
  { id: 'language:gnomish', label: 'Gnomish' },
  { id: 'language:goblin', label: 'Goblin' },
  { id: 'language:halfling', label: 'Halfling' },
  { id: 'language:orc', label: 'Orc' },
  { id: 'language:abyssal', label: 'Abyssal' },
  { id: 'language:celestial', label: 'Celestial' },
  { id: 'language:draconic', label: 'Draconic' },
  { id: 'language:deep speech', label: 'Deep Speech' },
  { id: 'language:infernal', label: 'Infernal' },
  { id: 'language:primordial', label: 'Primordial' },
  { id: 'language:sylvan', label: 'Sylvan' },
  { id: 'language:undercommon', label: 'Undercommon' },
];

// [P2.1]: All categories surfaced as one master union for the legacy-vs-known
//   round-trip split. Saved feats keep prerequisites as a plain string array
//   (matching the existing schema in srdFeats); the picker just constrains
//   which strings the player can author.
export const ALL_PREREQ_PROFICIENCIES = [
  ...PREREQ_SKILLS,
  ...PREREQ_TOOLS,
  ...PREREQ_ARMOR,
  ...PREREQ_WEAPONS,
  ...PREREQ_SAVES,
  ...PREREQ_LANGUAGES,
];

// ============================================================================
// CUSTOM CHOICES PRESETS (P2.1 — APR 2026)
// ============================================================================
// [PURPOSE]: One-click presets for the most common feat-choice patterns.
//   The `template` field is either an object (literal) or a function (lazy
//   resolver, so FIGHTING_STYLES is not eagerly walked unless the player
//   actually picks the Fighting Style preset).
export const CUSTOM_CHOICES_PRESETS = [
  {
    id: 'maneuvers',
    label: 'Battle Master Maneuvers',
    description: 'Player chooses N maneuvers from the Battle Master list.',
    template: { choose: 2, from: 'battlemaster_maneuvers' },
  },
  {
    id: 'fightingStyle',
    label: 'Fighting Styles',
    description: 'Player chooses N fighting styles from the SRD.',
    template: () => ({ choose: 1, from: Object.keys(FIGHTING_STYLES) }),
  },
  {
    id: 'cantrips',
    label: 'Cantrips (Magic Initiate style)',
    description: 'Player chooses N cantrips from a class spell list.',
    template: { choose: 2, from: 'class_cantrips' },
  },
  {
    id: 'damageType',
    label: 'Damage Types (Elemental Adept style)',
    description: 'Player chooses one damage type to specialize in.',
    template: { choose: 1, from: ['acid', 'cold', 'fire', 'lightning', 'thunder'] },
  },
];

// ----------------------------------------------------------------------------
// Dynamic preset extension — custom classes (Sub-plan 1B-iii — 2026-05-23)
// ----------------------------------------------------------------------------
// [PURPOSE]: Surface custom-class feature lists as one-click presets so a feat
//   author writing for a homebrew class can point at that class's feature
//   list the same way the SRD presets point at SRD lists.
//
// [WHY]: User audit 2026-05-19 — "feature lists" gap. The base 4 presets only
//   surface SRD content. A feat that should grant "choose 1 from Spellsword
//   features" had no preset and had to be authored as raw text.
//
// [SHAPE]: One dynamic preset per custom class with at least one feature.
//   Per-class preset:
//     id: `customClass_${classKey}_features`
//     label: `${className} Features`
//     description: `Player chooses N from the ${className} class's feature list.`
//     template: { choose: 1, from: [...flattened feature names across all levels] }
//
// [CALLER CONTRACT]: Use `getCustomChoicesPresets(playerStats)` when the
//   character context is in scope (preset RENDER + preset SAVE paths).
//   Keep the static `CUSTOM_CHOICES_PRESETS` export for id-only display
//   lookup when no context is available (e.g., rendering a saved feat's
//   preset chip in a static legend).

function buildCustomClassFeaturePreset(customClass) {
  const className = customClass && customClass.name;
  const classKey = (customClass && (customClass.id || customClass.name)) || null;
  const features = customClass && customClass.features;
  if (!className || !classKey || !features || typeof features !== 'object') return null;
  if (Array.isArray(features)) return null; // defensive — must be {level: [names]} map
  const flat = [];
  Object.values(features).forEach(levelArr => {
    if (Array.isArray(levelArr)) {
      levelArr.forEach(name => {
        if (typeof name === 'string' && name.trim()) flat.push(name);
      });
    }
  });
  if (flat.length === 0) return null;
  return {
    id: `customClass_${classKey}_features`,
    label: `${className} Features`,
    description: `Player chooses N from the ${className} class's feature list.`,
    template: { choose: 1, from: flat },
  };
}

export function getCustomChoicesPresets(playerStats) {
  const customClasses = (playerStats && Array.isArray(playerStats.customClasses))
    ? playerStats.customClasses
    : [];
  const dynamic = customClasses
    .map(buildCustomClassFeaturePreset)
    .filter(Boolean);
  return [...CUSTOM_CHOICES_PRESETS, ...dynamic];
}

// ============================================================================
// ACTIVE ABILITY ICONS (predefined emoji palette)
// ============================================================================
// [WHY]: Active abilities render with an icon next to the name. A curated
//   set (vs free emoji input) keeps the visual contract consistent and
//   prevents broken-glyph rendering on platforms missing exotic emoji.
export const ABILITY_ICONS = [
  '⚡', '🔥', '❄️', '💥', '✨', '🌟', '💫', '⭐',
  '⚔️', '🛡️', '🏹', '🗡️', '🪓', '🔨', '🏏', '🎯',
  '🍀', '🎲', '💎', '👁️', '🧙', '🦅', '🐉', '🦁',
  '💚', '💙', '💜', '🧠', '👊', '🌀', '🔮', '📿',
];

// ============================================================================
// WEAPONS LIST (Weapon Master feat support — NOV 2025)
// ============================================================================
// [WHY]: Weapon Master grants proficiency in 4 weapons chosen from this list.
//   Custom feats that want a similar shape can reuse this constant.
export const WEAPONS_LIST = {
  simple: [
    'Club', 'Dagger', 'Greatclub', 'Handaxe', 'Javelin',
    'Light Hammer', 'Mace', 'Quarterstaff', 'Sickle', 'Spear',
    'Crossbow (Light)', 'Dart', 'Shortbow', 'Sling',
  ],
  martial: [
    'Battleaxe', 'Flail', 'Glaive', 'Greataxe', 'Greatsword',
    'Halberd', 'Lance', 'Longsword', 'Maul', 'Morningstar',
    'Pike', 'Rapier', 'Scimitar', 'Shortsword', 'Trident',
    'War Pick', 'Warhammer', 'Whip',
    'Blowgun', 'Crossbow (Hand)', 'Crossbow (Heavy)', 'Longbow', 'Net',
  ],
};

// ============================================================================
// PASSIVE EFFECT CONDITIONS (Task 3)
// ============================================================================
// [PURPOSE]: Conditions that gate WHEN a passive effect applies. Mirrors
//   effectCalculator context keys — string mismatch here = effect never fires.
// NOTE: Legacy 4-entry flat list. ConditionKindSlot (Sub-plan 1H-H-cond) supersedes
//   this for NEW authoring while keeping backward-compat for existing saves.
export const EFFECT_CONDITIONS = [
  { value: 'always',       label: 'Always' },
  { value: 'unarmored',    label: 'When Unarmored' },
  { value: 'noShield',     label: 'When No Shield' },
  { value: 'dualWielding', label: 'When Dual Wielding' },
];

// ============================================================================
// CONDITION KIND SLOT — structured condition authoring (Sub-plan 1H-H-cond)
// ============================================================================
// [PURPOSE]: Replaces the flat EFFECT_CONDITIONS select with a kind-switched
//   composer. Each kind maps to a structured leaf shape on effect.condition.
// [PINNED CONTRACT]: ConditionKindSlot.emitCondition() maps internal {kind,...}
//   → {category, op, operands[]} OR {when: flag} (playerFlag backward-compat).
// [BREADCRUMB]: saveContext + targetType authors now; their runtime consumers
//   are Phase 2 (1H-H-cond GAP-8 / targetType arc). The evaluator will check
//   condition.category==='rollSourceType' and condition.category==='targetType'
//   once context enrichment lands in effectRegistry._buildContext.

export const CONDITION_KINDS = [
  { value: 'always',      label: 'Always (no condition)' },
  { value: 'playerFlag',  label: 'When player state...' },
  { value: 'weaponType',  label: 'When wielding weapon type...' },
  { value: 'armorType',   label: 'When wearing armor type...' },
  { value: 'saveContext', label: 'Save context (vs. ...)' },
  { value: 'damageType',  label: 'Damage type dealt' },
  { value: 'targetType',  label: 'Target creature type' },
  // [SUB-PLAN 1H-H-cond-combinators — 2026-05-29]: 1-level AND/OR/NOR combine.
  // The evaluator supports arbitrary depth; the UI authors 1-level only.
  // Nested combine slots are restricted to LEAF_KINDS to prevent unbounded depth.
  { value: 'combineAll',  label: 'Condition A AND Condition B' },
  { value: 'combineAny',  label: 'Condition A OR Condition B' },
  // [GREEN-COMPOSER-C — 2026-05-29]: none/NOR combinator — "NONE of these apply".
  // The evaluator already handles { none: [...] } (shipped in 1H-H-cond-combinators).
  // This closes the breadcrumb from cb6: UI now authors all 3 combinator kinds.
  { value: 'combineNone', label: 'NONE of these apply (NOR)' },
];

// Specific weapon base-type ids matching BASE_ITEMS.weapons keys in gameData.js.
// Used by ConditionKindSlot kind='weaponType'. These are BARE ids (not prefixed)
// because the runtime evaluator compares context.equippedWeaponBaseType directly.
// [BREADCRUMB]: These map only to SRD weapons. Custom weapon base types (created
//   via Content Workshop) are not yet surfaced here — follow-up when custom
//   weapon authoring lands (endgame generalization arc).
export const WEAPON_BASE_TYPES = [
  // Simple Melee
  { id: 'club',           label: 'Club' },
  { id: 'dagger',         label: 'Dagger' },
  { id: 'greatclub',      label: 'Greatclub' },
  { id: 'handaxe',        label: 'Handaxe' },
  { id: 'javelin',        label: 'Javelin' },
  { id: 'light_hammer',   label: 'Light Hammer' },
  { id: 'mace',           label: 'Mace' },
  { id: 'quarterstaff',   label: 'Quarterstaff' },
  { id: 'sickle',         label: 'Sickle' },
  { id: 'spear',          label: 'Spear' },
  // Simple Ranged
  { id: 'crossbow_light', label: 'Crossbow, Light' },
  { id: 'dart',           label: 'Dart' },
  { id: 'shortbow',       label: 'Shortbow' },
  { id: 'sling',          label: 'Sling' },
  // Martial Melee
  { id: 'battleaxe',      label: 'Battleaxe' },
  { id: 'flail',          label: 'Flail' },
  { id: 'glaive',         label: 'Glaive' },
  { id: 'greataxe',       label: 'Greataxe' },
  { id: 'greatsword',     label: 'Greatsword' },
  { id: 'halberd',        label: 'Halberd' },
  { id: 'lance',          label: 'Lance' },
  { id: 'longsword',      label: 'Longsword' },
  { id: 'maul',           label: 'Maul' },
  { id: 'morningstar',    label: 'Morningstar' },
  { id: 'pike',           label: 'Pike' },
  { id: 'rapier',         label: 'Rapier' },
  { id: 'scimitar',       label: 'Scimitar' },
  { id: 'shortsword',     label: 'Shortsword' },
  { id: 'trident',        label: 'Trident' },
  { id: 'war_pick',       label: 'War Pick' },
  { id: 'warhammer',      label: 'Warhammer' },
  { id: 'whip',           label: 'Whip' },
  // Martial Ranged
  { id: 'blowgun',        label: 'Blowgun' },
  { id: 'crossbow_hand',  label: 'Crossbow, Hand' },
  { id: 'crossbow_heavy', label: 'Crossbow, Heavy' },
  { id: 'longbow',        label: 'Longbow' },
  { id: 'net',            label: 'Net' },
];

// Save context options for kind='saveContext'. Values are the operands stored in
// condition.operands[] — the runtime evaluator checks these against a
// rollSourceType flag set at save-roll time (Phase 2 consumer).
export const SAVE_CONTEXT_OPTIONS = [
  { value: 'vs_spells',  label: 'vs. Spells' },
  { value: 'vs_poison',  label: 'vs. Poison' },
  { value: 'vs_fear',    label: 'vs. Fear' },
  { value: 'vs_charm',   label: 'vs. Charm' },
  { value: 'vs_str',     label: 'vs. Strength save' },
  { value: 'vs_dex',     label: 'vs. Dexterity save' },
  { value: 'vs_con',     label: 'vs. Constitution save' },
  { value: 'vs_int',     label: 'vs. Intelligence save' },
  { value: 'vs_wis',     label: 'vs. Wisdom save' },
  { value: 'vs_cha',     label: 'vs. Charisma save' },
];

// SRD creature type options for kind='targetType'. Values stored in
// condition.operands[] — runtime consumer is Phase 2 (targetType arc).
export const TARGET_TYPE_OPTIONS = [
  { value: 'humanoid',    label: 'Humanoid' },
  { value: 'undead',      label: 'Undead' },
  { value: 'fiend',       label: 'Fiend' },
  { value: 'beast',       label: 'Beast' },
  { value: 'dragon',      label: 'Dragon' },
  { value: 'aberration',  label: 'Aberration' },
  { value: 'construct',   label: 'Construct' },
  { value: 'elemental',   label: 'Elemental' },
  { value: 'celestial',   label: 'Celestial' },
  { value: 'fey',         label: 'Fey' },
  { value: 'giant',       label: 'Giant' },
  { value: 'monstrosity', label: 'Monstrosity' },
  { value: 'ooze',        label: 'Ooze' },
  { value: 'plant',       label: 'Plant' },
];

// ============================================================================
// AMMO TYPE OPTIONS — triggered-effect ammoTypeMatch filter (Sub-plan 1H-H-cond Phase 2)
// ============================================================================
// [PURPOSE]: Constrained pick list for the ammoTypeMatch filter key in
//   triggered-effect condition blocks (EffectBuilderControls.js). Values are the
//   ammoType tokens written to BASE_ITEMS.weapons (gameData.js:31,35,36,57-60)
//   and ammo items (gameData.js:490-573). These MUST match exactly what
//   _readTriggerContextField('ammoTypeMatch') reads from context.ammo.ammoType.
// [WHY]: Previously a free-text <input> — the S1 anti-pattern (free-text where
//   a catalog exists). The real values are a closed 4-entry set; free-text lets
//   authors type 'arrow' when the runtime uses 'arrows', silently missing.
export const AMMO_TYPE_OPTIONS = [
  { id: 'arrows',          label: 'Arrows' },
  { id: 'crossbow_bolts',  label: 'Crossbow Bolts' },
  { id: 'sling_bullets',   label: 'Sling Bullets' },
  { id: 'blowgun_needles', label: 'Blowgun Needles' },
];

// ============================================================================
// DAMAGE TYPES — feat-creator local list (Task 6)
// ============================================================================
// [WHY]: This list intentionally includes the composite phrase
//   "nonmagical bludgeoning, piercing, and slashing" as a 14th entry (used by
//   resistances like Heavy Armor Master). It does NOT match the canonical
//   DAMAGE_TYPES enum in src/constants/enums.js exactly — the canonical enum
//   is for ITEM damage rolls, this one is for RESISTANCE grants. Keep both.
// [Rule 22 note]: Named DAMAGE_TYPES_FEAT (not DAMAGE_TYPES) to prevent
//   import-collision with `import { DAMAGE_TYPES } from '../constants/enums'`
//   in FeatsPanel.js. Consumers import the one they actually need.
export const DAMAGE_TYPES_FEAT = [
  'acid', 'bludgeoning', 'cold', 'fire', 'force', 'lightning',
  'necrotic', 'piercing', 'poison', 'psychic', 'radiant', 'slashing',
  'thunder', 'nonmagical bludgeoning, piercing, and slashing',
];

// [SUB-PLAN 2B WAVE-2 — 2026-05-25]: Pure damage-source types (13). What a
//   spell or weapon actually *deals*. Does NOT include the "nonmagical
//   bludgeoning, piercing, and slashing" composite — that string is meaningful
//   only as a resistance/immunity scope (e.g. Stoneskin grants resistance to
//   nonmagical physical damage), NOT as a damage source. Spells are magical
//   by definition.
// [WHY]: The composer registry's 'damageType' kind previously emitted the
//   14-entry resistance list. Spell authoring pickers need source-only.
//   `DAMAGE_TYPES_FEAT` retained for resistance-scope consumers (Damage
//   Resistance Grants, Immunities, etc.).
export const DAMAGE_SOURCE_TYPES = [
  'acid', 'bludgeoning', 'cold', 'fire', 'force', 'lightning',
  'necrotic', 'piercing', 'poison', 'psychic', 'radiant', 'slashing',
  'thunder',
];

// ============================================================================
// ADVANTAGE TARGETS (Task 7)
// ============================================================================
// [PURPOSE]: Targets a feat can grant advantage on. Values are the runtime
//   keys the advantage checker queries — DO NOT freeform; mismatch = silent
//   no-op at roll-time.
export const ADVANTAGE_TARGETS = [
  { value: 'initiative', label: 'Initiative' },
  { value: 'stealth', label: 'Stealth checks' },
  { value: 'perception', label: 'Perception checks' },
  { value: 'deception', label: 'Deception checks' },
  { value: 'persuasion', label: 'Persuasion checks' },
  { value: 'athletics', label: 'Athletics checks' },
  { value: 'strSave', label: 'Strength saves' },
  { value: 'dexSave', label: 'Dexterity saves' },
  { value: 'conSave', label: 'Constitution saves' },
  { value: 'intSave', label: 'Intelligence saves' },
  { value: 'wisSave', label: 'Wisdom saves' },
  { value: 'chaSave', label: 'Charisma saves' },
  { value: 'deathSave', label: 'Death saving throws' },
  { value: 'int_wis_cha_magic_saves', label: 'INT/WIS/CHA saves vs. magic' },
  // [1H-I - Stone-2 sweep 2026-05-29] SRD dragon-save advantage targets. Values MUST equal the
  //   BASE_ITEMS dragon_scale_mail effect targets ('save:dragon_*') for ReferencePicker round-trip
  //   parity. Per the runtime-key contract above: these are CONTEXTUAL (no automated roll queries
  //   them - the player applies the advantage when a dragon frightful-presence/breath save comes
  //   up), so they are surfaced + stored, not auto-fired. Closes the 10 dragon_scale_mail_* ui gaps.
  { value: 'save:dragon_frightful_presence', label: 'Dragon Frightful Presence saves' },
  { value: 'save:dragon_breath_weapon', label: 'Dragon breath weapon saves' },
];

// ============================================================================
// SENSE TYPES (Task 8)
// ============================================================================
// [PURPOSE]: Senses a feat can grant. All four entries use a range field
//   (in feet); `hasRange: true` is the renderer hint for the range input.
export const SENSE_TYPES = [
  { value: 'darkvision',  label: 'Darkvision',  hasRange: true },
  { value: 'blindsight',  label: 'Blindsight',  hasRange: true },
  { value: 'truesight',   label: 'Truesight',   hasRange: true },
  { value: 'tremorsense', label: 'Tremorsense', hasRange: true },
];

// ============================================================================
// FORMULA OPTIONS — passive-effect formula tokens (Task 4)
// ============================================================================
// [PURPOSE]: When a passive effect uses a formula (not a fixed number), the
//   author picks one of these tokens. effectRegistry._getValue resolves the
//   token at calc-time against the live character (so STR mod tracks ASI).
//
// [SUB-PLAN 3D-S2 — 2026-05-28]: Added 5 structured scaling tokens.
//   parametricKind signals that the picker should surface a sub-picker for
//   `target` (e.g., classLevel needs a class name; resourceSpent needs a pool key).
//   The evaluator in effectRegistry._getValue object-value branch handles all tokens.
export const FORMULA_OPTIONS = [
  { value: 'str_modifier',              label: 'STR mod' },
  { value: 'dex_modifier',              label: 'DEX mod' },
  { value: 'con_modifier',              label: 'CON mod' },
  { value: 'int_modifier',              label: 'INT mod' },
  { value: 'wis_modifier',              label: 'WIS mod' },
  { value: 'cha_modifier',              label: 'CHA mod' },
  { value: 'proficiency_bonus',         label: 'Prof bonus' },
  // [3D-S2] New structured scaling tokens
  { value: 'characterLevel',            label: 'Character level' },
  { value: 'classLevel',                label: 'Class level',              parametricKind: 'class' },
  { value: 'spellSlotLevelExpended',    label: 'Spell slot level expended' },
  { value: 'resourceSpent',             label: 'Resource spent',           parametricKind: 'resourcePool' },
  { value: 'proficiencyTier',           label: 'Proficiency tier (1-4)' },
];

// ============================================================================
// FORMULA-DEFAULT ATTRIBUTES (Sub-plan 1B-ii — 2026-05-23)
// ============================================================================
// [PURPOSE]: Attributes where formula-mode (useFormula=true) is the more
//   common authoring intent — i.e., where a feat would commonly add
//   +ability_mod or +proficiency_bonus rather than a flat numeric value.
//   When the passive-effect attribute dropdown changes to one of these,
//   CustomFeatCreator auto-flips useFormula to true so the FORMULA_OPTIONS
//   dropdown appears instead of the numeric input. ƒ button preserves the
//   manual-override path.
// [WHY]: Pre-fix every attribute pick required the author to also click the
//   ƒ toggle for the most common patterns (+PB to-hit, +ability to AC,
//   +ability to skill). Friction across the dominant feat-authoring shapes.
// [HEURISTIC]:
//   - Formula-default: total-roll attributes a feat commonly grants a
//     formula bonus to — combat totals, spellcasting bonuses, all 6 saves,
//     all 18 skills, passivePerception.
//   - Literal-default (NOT in this set): attributes a feat commonly grants
//     a flat numeric value to — hp, the 6 ability scores, the 4 movement
//     speeds, proficiencyBonus.
// [RULE 11]: Set-based lookup. Adding/removing entries here updates every
//   consumer automatically.
export const FORMULA_DEFAULT_ATTRIBUTES = new Set([
  // Combat totals
  'attackBonus', 'damageBonus', 'ac', 'initiative',
  // Spellcasting bonuses
  'spellSaveDC', 'spellAttackBonus',
  // Passive senses
  'passivePerception',
  // All 6 saves
  'strSave', 'dexSave', 'conSave', 'intSave', 'wisSave', 'chaSave',
  // All 18 skills
  'acrobatics', 'animalHandling', 'arcana', 'athletics',
  'deception', 'history', 'insight', 'intimidation',
  'investigation', 'medicine', 'nature', 'perception',
  'performance', 'persuasion', 'religion',
  'sleightOfHand', 'stealth', 'survival',
]);

export function shouldDefaultToFormula(attribute) {
  if (!attribute || typeof attribute !== 'string') return false;
  return FORMULA_DEFAULT_ATTRIBUTES.has(attribute);
}

// ============================================================================
// CREATURE SIZES — canonical D&D 5e size vocab (Sub-plan 2F kind (c) — 2026-05-25)
// ============================================================================
// [PURPOSE]: Single source of truth for creature/race/component size vocabulary.
//   Used by the composer registry's `creatureSize` kind so all three surfaces
//   (MonsterEditor size, CustomRaceCreator race.size, CustomSpellCreator
//   material-component size) share one source of truth.
// [SHAPE]: { id, label } — same shape as all other customFeatVocabulary entries.
//   `id` values are Title-Case strings matching the existing storage shape in all
//   three consumers — no data migration needed.
// [VOCAB EXPANSION]: CustomRaceCreator previously had ONLY 2 hardcoded options
//   (Small, Medium). This export intentionally expands to the full 6-entry vocab
//   (Tiny/Small/Medium/Large/Huge/Gargantuan) — parallel to rechargeOn's 2→4
//   expansion in CustomClassCreator. Allows authoring tiny pixie races or huge
//   goliath-variant races through the Race Creator.
export const CREATURE_SIZES = [
  { id: 'Tiny',       label: 'Tiny' },
  { id: 'Small',      label: 'Small' },
  { id: 'Medium',     label: 'Medium' },
  { id: 'Large',      label: 'Large' },
  { id: 'Huge',       label: 'Huge' },
  { id: 'Gargantuan', label: 'Gargantuan' },
];

// ============================================================================
// RECHARGE OPTIONS — resource / ability recharge vocab (Sub-plan 2F — 2026-05-25)
// ============================================================================
// [PURPOSE]: Canonical recharge options for feat active-abilities and class
//   resource pools. Used by the composer registry's `rechargeOn` kind so all
//   three creator surfaces (CustomFeatCreator activeAbility, CustomFeatCreator
//   resourcePool, CustomClassCreator resource) share one source of truth.
// [SHAPE]: { id, label } — same shape as all other customFeatVocabulary entries.
//   `id` values are the runtime keys saved to the data shape (unchanged from
//   prior inline constant — no storage migration needed).
// [SCOPE]: Resource/ability recharge only. Item-scheduled recharges (consumable,
//   weekly, monthly, none) live in src/data/rechargeTypes.js and are out of
//   scope here — those serve a different domain (ItemDialog).
// [WHY NOT RECHARGE_TYPES superset]: Including consumable/weekly/monthly/none
//   in a feat/class-resource picker is semantically wrong for D&D mechanics.
//   The 4-entry scope below covers the D&D SRD resource-reset triggers.
export const RECHARGE_OPTIONS = [
  { id: 'short_rest', label: 'Short Rest' },
  { id: 'long_rest',  label: 'Long Rest' },
  { id: 'dawn',       label: 'Dawn (per day)' },
  { id: 'manual',     label: 'Manual' },
];

// ============================================================================
// DIE SIZES — generic resource/damage die picker (Sub-plan 2F kind d — 2026-05-25)
// ============================================================================
// [PURPOSE]: Canonical die-size vocab for feat resource-pool die fields and any
//   other surface that needs to pick a "what die" without a quantity prefix.
// [SHAPE]: { id, label } — bare 'd{N}' shape. id matches the storage key written
//   by CustomFeatCreator (feat.resourcePool.dieSize = 'd6').
// [SCOPE]: 5 entries covering the D&D 5e resource/damage die range. d20 is
//   intentionally excluded — it is the check die, not a resource or damage die.
// [WHY NOT d20]: Preserving the pre-existing CustomFeatCreator vocab exactly
//   (prior inline array was ['d4','d6','d8','d10','d12'] — no d20). Rule 30c
//   pre-emptive split: classHitDie uses a DIFFERENT vocab (4 entries, '1d{N}'
//   shape) because PHB-class hit dice are a distinct semantic domain.
export const DIE_SIZES = Object.freeze([
  { id: 'd4',  label: 'd4' },
  { id: 'd6',  label: 'd6' },
  { id: 'd8',  label: 'd8' },
  { id: 'd10', label: 'd10' },
  { id: 'd12', label: 'd12' },
]);

// ============================================================================
// CLASS HIT DICE — PHB-constrained class hit-die picker (Sub-plan 2F kind d — 2026-05-25)
// ============================================================================
// [PURPOSE]: Canonical hit-die vocab for the CustomClassCreator Hit Dice field.
//   Replaces the inline HIT_DICE_OPTIONS const at L80 of CustomClassCreator.js.
// [SHAPE]: { id, label } — '1d{N}'-prefixed shape matching the storage key written
//   by CustomClassCreator (classData.hitDice = '1d10'). The PHB always writes
//   class hit dice as '1dN per level' (e.g. "1d8 hit die", PHB p.57).
// [SCOPE]: 4 entries covering PHB-defined class hit dice. d4 excluded (too frail
//   for any official 5e class). d20 excluded (not a class hit die).
// [WHY SEPARATE from DIE_SIZES]: vocab size (4 vs 5), vocab shape ('1d6' vs 'd6'),
//   and semantic domain (PHB class hit die vs generic resource/damage die) all
//   diverge — Rule 30c pre-emptive split. Conflating them would require either an
//   adapter on every consumer or a storage migration.
export const CLASS_HIT_DICE = Object.freeze([
  { id: '1d6',  label: '1d6' },
  { id: '1d8',  label: '1d8' },
  { id: '1d10', label: '1d10' },
  { id: '1d12', label: '1d12' },
]);

// ============================================================================
// SPELL SCHOOLS — PHB eight schools of magic (Sub-plan 2F kind (e) — 2026-05-25)
// ============================================================================
// [PURPOSE]: Single source of truth for spell school vocabulary in CustomSpellCreator.
//   Retires inline `const schools` array at L127-130 of CustomSpellCreator.js.
// [SHAPE]: { id, label } — id === label (display-string-as-id). Storage shape
//   unchanged: spell.school = 'Evocation' (Title-Case string verbatim). No data migration.
// [SCOPE]: The canonical 8 D&D 5e PHB schools of magic (PHB p.203).
export const SPELL_SCHOOLS = Object.freeze([
  { id: 'Abjuration',    label: 'Abjuration' },
  { id: 'Conjuration',   label: 'Conjuration' },
  { id: 'Divination',    label: 'Divination' },
  { id: 'Enchantment',   label: 'Enchantment' },
  { id: 'Evocation',     label: 'Evocation' },
  { id: 'Illusion',      label: 'Illusion' },
  { id: 'Necromancy',    label: 'Necromancy' },
  { id: 'Transmutation', label: 'Transmutation' },
]);

// ============================================================================
// CASTING TIMES — PHB spell-casting-time options (Sub-plan 2F kind (e) — 2026-05-25)
// ============================================================================
// [PURPOSE]: Single source of truth for casting-time vocabulary in CustomSpellCreator.
//   Retires inline `const castingTimes` array at L132-135 of CustomSpellCreator.js.
// [SHAPE]: { id, label } — id === label (display-string-as-id). Storage shape
//   unchanged: spell.castingTime = '1 action' (exact string verbatim). No data migration.
// [SEMANTIC DISTINCTNESS (Rule 30c)]: castingTime = COST to cast a spell (how long
//   the casting takes). Even though '1 minute', '1 hour', '8 hours', '24 hours' appear
//   in BOTH castingTime and spellDuration, they mean DIFFERENT things in each context.
//   These are SEPARATE kinds — do not merge them.
// [SCOPE]: 9 PHB-standard casting-time options.
export const CASTING_TIMES = Object.freeze([
  { id: '1 action',      label: '1 action' },
  { id: '1 bonus action', label: '1 bonus action' },
  { id: '1 reaction',    label: '1 reaction' },
  { id: '1 minute',      label: '1 minute' },
  { id: '10 minutes',    label: '10 minutes' },
  { id: '1 hour',        label: '1 hour' },
  { id: '8 hours',       label: '8 hours' },
  { id: '12 hours',      label: '12 hours' },
  { id: '24 hours',      label: '24 hours' },
]);

// ============================================================================
// SPELL RANGES — PHB spell-range options (Sub-plan 2F kind (e) — 2026-05-25)
// ============================================================================
// [PURPOSE]: Single source of truth for spell-range vocabulary in CustomSpellCreator.
//   Retires inline `const ranges` array at L137-141 of CustomSpellCreator.js.
// [SHAPE]: { id, label } — id === label (display-string-as-id). Storage shape
//   unchanged: spell.range = '60 feet' (exact string verbatim). No data migration.
// [SCOPE]: 15 PHB-standard range options: Self, Touch, distance bands, Sight, Unlimited, Special.
export const SPELL_RANGES = Object.freeze([
  { id: 'Self',       label: 'Self' },
  { id: 'Touch',      label: 'Touch' },
  { id: '5 feet',     label: '5 feet' },
  { id: '10 feet',    label: '10 feet' },
  { id: '30 feet',    label: '30 feet' },
  { id: '60 feet',    label: '60 feet' },
  { id: '90 feet',    label: '90 feet' },
  { id: '120 feet',   label: '120 feet' },
  { id: '150 feet',   label: '150 feet' },
  { id: '300 feet',   label: '300 feet' },
  { id: '500 feet',   label: '500 feet' },
  { id: '1 mile',     label: '1 mile' },
  { id: 'Sight',      label: 'Sight' },
  { id: 'Unlimited',  label: 'Unlimited' },
  { id: 'Special',    label: 'Special' },
]);

// ============================================================================
// SPELL DURATIONS — PHB spell-duration options (Sub-plan 2F kind (e) — 2026-05-25)
// ============================================================================
// [PURPOSE]: Single source of truth for spell-duration vocabulary in CustomSpellCreator.
//   Retires inline `const durations` array at L143-147 of CustomSpellCreator.js.
// [SHAPE]: { id, label } — id === label (display-string-as-id). Storage shape
//   unchanged: spell.duration = 'Instantaneous' (exact string verbatim). No data migration.
// [SEMANTIC DISTINCTNESS (Rule 30c)]: spellDuration = HOW LONG the effect persists after
//   casting. Even though '1 minute', '1 hour', '8 hours', '24 hours' appear in BOTH
//   castingTime and spellDuration, they mean DIFFERENT things in each context.
//   These are SEPARATE kinds — do not merge them.
// [SCOPE]: 12 PHB-standard duration options.
export const SPELL_DURATIONS = Object.freeze([
  { id: 'Instantaneous',  label: 'Instantaneous' },
  { id: 'Until dispelled', label: 'Until dispelled' },
  { id: '1 round',        label: '1 round' },
  { id: '1 minute',       label: '1 minute' },
  { id: '10 minutes',     label: '10 minutes' },
  { id: '1 hour',         label: '1 hour' },
  { id: '8 hours',        label: '8 hours' },
  { id: '24 hours',       label: '24 hours' },
  { id: '7 days',         label: '7 days' },
  { id: '10 days',        label: '10 days' },
  { id: '30 days',        label: '30 days' },
  { id: 'Special',        label: 'Special' },
]);

// ============================================================================
// CASTER TYPES — D&D 5e spell-slot progression categories (Sub-plan 2F kind (f) — 2026-05-25)
// ============================================================================
// [PURPOSE]: Single source of truth for caster-type vocabulary in CustomClassCreator.
//   Retires inline `const CASTER_TYPES = [...]` array at L121 of CustomClassCreator.js.
// [SHAPE]: { id, label } — id === label (display-string-as-id). Storage shape
//   unchanged: classData.spellcasting.spellSlots = 'Full caster' (exact string verbatim).
//   NOTE: the storage field is historically named `spellSlots` but its value IS the
//   caster-type string. The misnaming is kept as-is — renaming cascades across save/load
//   and classResources.js consumers; out of scope for this commit.
// [SEMANTIC DISTINCTNESS (Rule 30c)]: casterType describes HOW a class gains spell slots
//   (full progression / half / third / pact). spellPrepType describes HOW a class manages
//   known spells (known list / prepared list / spellbook). Two separate kinds.
// [SCOPE]: 4 PHB-standard caster-type categories + 1 At-will entry (Sub-plan A49 — 2026-05-30).
//   [id===label contract preserved — display-string-as-id like all other CASTER_TYPES entries.]
//   At-will casters have NO spell slots. All spells are granted via 'atWillSpell' effect rows
//   on class features. Reader: effectCalculator.getAtWillSpells (effectCalculator.js:3328).
//   UI callout in CustomClassCreator hides slot/cantrip tables for At-will (data-testid="at-will-caster-callout").
export const CASTER_TYPES = Object.freeze([
  { id: 'Full caster',  label: 'Full caster' },
  { id: 'Half caster',  label: 'Half caster' },
  { id: 'Third caster', label: 'Third caster' },
  { id: 'Pact magic',   label: 'Pact magic' },
  { id: 'At-will',      label: 'At-will' },
]);

// ============================================================================
// SPELL PREP TYPES — D&D 5e spell-management modes (Sub-plan 2F kind (f) — 2026-05-25)
// ============================================================================
// [PURPOSE]: Single source of truth for spell-preparation-type vocabulary in CustomClassCreator.
//   Retires inline `const SPELL_PREP_TYPES = [{ value, label }, ...]` at L123-127 of
//   CustomClassCreator.js. The prior shape used `value`/`label` — normalized to `id`/`label`
//   to match the composer registry contract.
// [SHAPE]: { id, label } — id ≠ label (ids are short storage keys; labels are verbose
//   canonical-class references that help authors pick the right mode). The verbose labels
//   are intentional: they show which official 5e classes use each preparation mode.
// [SEMANTIC DISTINCTNESS (Rule 30c)]: spellPrepType describes WHICH spell management mode
//   a class uses (known list / daily preparation / spellbook). casterType describes WHICH
//   slot-progression curve the class follows. They are orthogonal — Warlock is a Pact magic
//   caster that uses a 'known' prep type; Paladin is a Half caster that uses 'prepared'.
//   Two separate kinds.
// [SCOPE]: 3 PHB-standard spell-preparation modes.
export const SPELL_PREP_TYPES = Object.freeze([
  { id: 'known',     label: 'Known Spells (Bard, Sorcerer, Ranger, Warlock)' },
  { id: 'prepared',  label: 'Prepared Spells (Cleric, Druid, Paladin)' },
  { id: 'spellbook', label: 'Spellbook (Wizard)' },
]);

// ============================================================================
// ITEM RARITIES — magic item rarity tiers (Sub-plan 2F kind (g) — 2026-05-25)
// ============================================================================
// [PURPOSE]: 7-tier rarity vocabulary for the ItemDialog rarity picker.
//   Empty-string id preserves "No Rarity (Mundane)" for non-magical authoring.
//   id ≠ label: ids are lowercase snake_case ('very_rare'); labels are Title Case.
// [WHY]: Replaces inline <option> block in ItemDialog L1421-1433.
//   Kind='rarity' (AC-computation domain). Distinct from kind='armor' proficiency vocab.
export const ITEM_RARITIES = Object.freeze([
  { id: '',          label: 'No Rarity (Mundane)' },
  { id: 'common',    label: 'Common' },
  { id: 'uncommon',  label: 'Uncommon' },
  { id: 'rare',      label: 'Rare' },
  { id: 'very_rare', label: 'Very Rare' },
  { id: 'legendary', label: 'Legendary' },
  { id: 'artifact',  label: 'Artifact' },
]);

// ============================================================================
// STAT MOD TARGETS — item stat-modification targets (Item shape Sub-plan Stone 1 — 2026-05-25)
// ============================================================================
// [PURPOSE]: 18-entry vocabulary for the StatModificationsList component and the
//   `statModTarget` composer kind. Covers the three operations (set / add / subtract)
//   across the 6 D&D 5e ability scores.
// [SHAPE]: { id, label, meta: { group } } — `meta.group` is the optgroup label
//   for the ReferencePicker renderer (parallel to kind='sense' using meta.hasRange).
//   Three groups mirror the existing ItemDialog L1237/L1245/L1253 optgroups exactly
//   so the upcoming wiring (Stone 4) reproduces the same visual organization.
// [ID FORMAT — CRITICAL]: IDs are camelCase operation+Ability strings
//   (e.g. 'setStrength', 'addCharisma', 'subtractConstitution'). The runtime
//   `emitAbilityRow` function in itemEffectsRegistration.js parses these via
//   regex `/^(set|add|subtract)(.+)$/i`. DO NOT change the id format — no
//   data migration is needed because the ids match the storage strings already
//   written by ItemDialog and EditItemDialog.
// [MULTI-SYSTEM NOTE]: The operation prefix (set/add/subtract) is reasonably
//   system-agnostic; the ability names are D&D-5e-specific. Future multi-system
//   extension can swap the vocab source via the kind dispatch without changing the
//   StatModificationsList component.
// [BREADCRUMB — Stone 4]: ItemDialog will wire to this kind (replacing its inline
//   optgroup block at L1237-1260) in Stone 4 of the Item shape harmonization Sub-plan.
// [BREADCRUMB — Stone 5]: EditItemDialog will wire to this kind in Stone 5.
export const STAT_MOD_TARGETS = Object.freeze([
  // ---- Set Stat To (6 entries) ----
  { id: 'setStrength',        label: 'Set Strength',           meta: { group: 'Set Stat To' } },
  { id: 'setDexterity',       label: 'Set Dexterity',          meta: { group: 'Set Stat To' } },
  { id: 'setConstitution',    label: 'Set Constitution',       meta: { group: 'Set Stat To' } },
  { id: 'setIntelligence',    label: 'Set Intelligence',       meta: { group: 'Set Stat To' } },
  { id: 'setWisdom',          label: 'Set Wisdom',             meta: { group: 'Set Stat To' } },
  { id: 'setCharisma',        label: 'Set Charisma',           meta: { group: 'Set Stat To' } },
  // ---- Add To Stat (6 entries) ----
  { id: 'addStrength',        label: 'Add to Strength',        meta: { group: 'Add To Stat' } },
  { id: 'addDexterity',       label: 'Add to Dexterity',       meta: { group: 'Add To Stat' } },
  { id: 'addConstitution',    label: 'Add to Constitution',    meta: { group: 'Add To Stat' } },
  { id: 'addIntelligence',    label: 'Add to Intelligence',    meta: { group: 'Add To Stat' } },
  { id: 'addWisdom',          label: 'Add to Wisdom',          meta: { group: 'Add To Stat' } },
  { id: 'addCharisma',        label: 'Add to Charisma',        meta: { group: 'Add To Stat' } },
  // ---- Subtract From Stat (6 entries) ----
  { id: 'subtractStrength',    label: 'Subtract from Strength',    meta: { group: 'Subtract From Stat' } },
  { id: 'subtractDexterity',   label: 'Subtract from Dexterity',   meta: { group: 'Subtract From Stat' } },
  { id: 'subtractConstitution', label: 'Subtract from Constitution', meta: { group: 'Subtract From Stat' } },
  { id: 'subtractIntelligence', label: 'Subtract from Intelligence', meta: { group: 'Subtract From Stat' } },
  { id: 'subtractWisdom',      label: 'Subtract from Wisdom',      meta: { group: 'Subtract From Stat' } },
  { id: 'subtractCharisma',    label: 'Subtract from Charisma',    meta: { group: 'Subtract From Stat' } },
]);

// ============================================================================
// ARMOR TYPES AC — armor type for AC computation (Sub-plan 2F kind (g) — 2026-05-25)
// ============================================================================
// [PURPOSE]: 4 armor types with verbose AC-formula labels for the ItemDialog
//   armor type picker. Named ARMOR_TYPES_AC (with _AC suffix) to distinguish
//   from PREREQ_ARMOR (proficiency categories like 'light_armor'/'medium_armor').
//   Semantic domain: "what's the AC formula?" NOT "can you wear it?"
//   id ≠ label: ids are bare lowercase ('light'/'medium'/'heavy'/'shield');
//   labels include the AC formula so authors understand the effect.
// [WHY]: Replaces inline <option> block in ItemDialog L1110-1113.
//   Kind='armorType' (AC-computation). Distinct from kind='armor' (proficiency).
//   NO data migration needed — storage shape unchanged ('light'/'medium'/'heavy'/'shield').
export const ARMOR_TYPES_AC = Object.freeze([
  { id: 'light',  label: 'Light (AC = DEX + base, no cap)' },
  { id: 'medium', label: 'Medium (AC = DEX capped +2 + base)' },
  { id: 'heavy',  label: 'Heavy (AC = base, no DEX bonus)' },
  { id: 'shield', label: 'Shield (+2 AC bonus)' },
]);

// ============================================================================
// ALIGNMENT TYPES — monster (and NPC) alignment vocabulary (STONE A — 2026-06-05)
// ============================================================================
// [PURPOSE]: The `alignment` composer kind for the MonsterEditor alignment
//   picker (2M-EDITOR-ALIGNMENT-HYBRID). Replaces the hardcoded 10-entry
//   ALIGNMENTS const in MonsterEditor.js. id === label (canonical casing) —
//   alignment is stored as the display string on monster.alignment (display-only
//   field; no semantic consumer branches on it — MonsterSheet.js:208 passthrough).
// [WHY]: 15 SRD roster monsters store "Any …" meta-alignment prose the old
//   10-entry <select> could not represent → authoring silently lost them (the
//   only roster round-trip gap class). The "Any …" set is a KNOWN, FINITE SRD
//   vocabulary, so per the no-textbox authoring bar it MUST be a selector, not
//   free-text. Genuinely-open prose (e.g. werecreature split-probability
//   "Neutral Good (50%) or Neutral Evil (50%)") is handled by the picker's
//   allowMint "+ add" affordance, NOT an always-on textbox.
// [SHAPE]: { id, label }, id === label. Storage shape unchanged (plain string)
//   → no data migration. (Two baseMonsters case-variants — "Any Evil",
//   "Any Alignment" — are normalized to the canonical casing in the same arc.)
// [MULTI-SYSTEM NOTE — endgame keystone]: This is the D&D specialization of the
//   "offer the active rule-system's vocabulary, allow prose where it's prose-y"
//   substrate. Daggerheart has NO alignment; CoC uses sanity. The PATTERN
//   (kind-dispatched vocab + allowMint + synthetic-value preservation in
//   ReferencePicker) is RSE-reusable; this vocab is the D&D content. The
//   registry `kind` is the seam — no D&D shortcut locked in.
export const ALIGNMENT_TYPES = Object.freeze([
  // ---- The nine canonical alignments + Unaligned ----
  { id: 'Lawful Good',     label: 'Lawful Good' },
  { id: 'Neutral Good',    label: 'Neutral Good' },
  { id: 'Chaotic Good',    label: 'Chaotic Good' },
  { id: 'Lawful Neutral',  label: 'Lawful Neutral' },
  { id: 'Neutral',         label: 'Neutral' },
  { id: 'Chaotic Neutral', label: 'Chaotic Neutral' },
  { id: 'Lawful Evil',     label: 'Lawful Evil' },
  { id: 'Neutral Evil',    label: 'Neutral Evil' },
  { id: 'Chaotic Evil',    label: 'Chaotic Evil' },
  { id: 'Unaligned',       label: 'Unaligned' },
  // ---- SRD-attested "Any …" meta-alignments ----
  //   VERIFIED 2026-06-05 against the open5e SRD monster corpus
  //   (api.open5e.com/v1/monsters/?document__slug=wotc-srd): these 5 are the
  //   ONLY meta-alignment strings the SRD bestiary actually uses —
  //   any→acolyte, any non-good→assassin, any non-lawful→bandit,
  //   any chaotic→berserker, any evil→lich. Other grammatically-valid
  //   meta-alignments ("any good alignment", "any non-evil alignment", …) and
  //   werecreature split-probability prose are NOT in the SRD subset, so they
  //   stay OUT of this constrained vocab and are authored through the picker's
  //   allowMint "+ add" affordance instead. Factual-data rule: every entry here
  //   is provable against the machine-readable authoritative source, not LLM
  //   memory (which had over-included 5 unattested strings — caught in verify).
  { id: 'Any alignment',            label: 'Any alignment' },
  { id: 'Any non-good alignment',   label: 'Any non-good alignment' },
  { id: 'Any non-lawful alignment', label: 'Any non-lawful alignment' },
  { id: 'Any chaotic alignment',    label: 'Any chaotic alignment' },
  { id: 'Any evil alignment',       label: 'Any evil alignment' },
]);

// [STONE A pt2 — 2026-06-07 · 2M-EDITOR-VOCAB-PICKERS]: Creature SPOKEN-language
//   vocabulary for the MonsterEditor languages field (composer kind
//   'spokenLanguage'). Bare Title-case `id===label` to MATCH the stored shape
//   (`monster.languages` = string[] of bare Title-case tokens, e.g. 'Common').
//
// [WHY A NEW KIND — not reuse `language`]: the feat-prereq `language` kind
//   (PREREQ_LANGUAGES) uses PREFIXED-LOWERCASE ids ('language:common') and is
//   LOCKED by the feat-prereq consumer (CustomFeatCreator/FeatsPanel) + its own
//   ReferencePicker.test.js suite. Mutating it would break those; `valueShape`
//   only strips the prefix to LOWERCASE ('common'), mismatching the stored
//   Title-case ('Common'). Feat-prereq "language requirement" and creature
//   "spoken-language list" are semantically distinct storage domains → a
//   separate kind is correct (Rule 30c kind-split), not a workaround.
//
// [MEMBERS]: the PHB/SRD standard + exotic + secret languages, the four
//   Primordial dialects (Auran/Aquan/Ignan/Terran — SRD stat-block convention),
//   plus every CLEAN single-token language the shipped roster uses (so each is
//   SELECTABLE, never minted-as-prose). Genuine prose — telepathy, "languages
//   it knew in life", "Any Six Languages", "Giant Eagle" — is authored through
//   the picker's allowMint "+ add" affordance (the no-textbox-compliant escape),
//   NOT a free-text-for-known-vocab box.
//
// [FACTUAL-DATA RULE]: this vocab is reconciled against the SRD/PHB language
//   table + the actual roster token set (grep over baseMonsters.js). Every entry
//   is a real D&D language OR a roster-attested token; none are LLM-invented.
//   (Note: 'Gnoll' is roster-attested — the roster models gnoll speech as its own
//   token; gnolls canonically understand Abyssal. Kept so existing data is
//   authorable; a data-normalization review is a separate factual-correction
//   concern, not this authoring-control change.)
//
// [MULTI-SYSTEM NOTE — RSE seam]: the D&D specialization of a system-agnostic
//   "communicationMode" vocabulary. Daggerheart/CoC have their own communication
//   models. The registry `kind` is the seam — RSE swaps the vocab, not the
//   consumer. Generalization tracked: master-queue `2M-spokenLanguage-rse-breadcrumb`.
export const SPOKEN_LANGUAGES = Object.freeze([
  // ---- Standard languages (PHB) ----
  { id: 'Common',       label: 'Common' },
  { id: 'Dwarvish',     label: 'Dwarvish' },
  { id: 'Elvish',       label: 'Elvish' },
  { id: 'Giant',        label: 'Giant' },
  { id: 'Gnomish',      label: 'Gnomish' },
  { id: 'Goblin',       label: 'Goblin' },
  { id: 'Halfling',     label: 'Halfling' },
  { id: 'Orc',          label: 'Orc' },
  // ---- Exotic languages (PHB) ----
  { id: 'Abyssal',      label: 'Abyssal' },
  { id: 'Celestial',    label: 'Celestial' },
  { id: 'Deep Speech',  label: 'Deep Speech' },
  { id: 'Draconic',     label: 'Draconic' },
  { id: 'Infernal',     label: 'Infernal' },
  { id: 'Primordial',   label: 'Primordial' },
  { id: 'Sylvan',       label: 'Sylvan' },
  { id: 'Undercommon',  label: 'Undercommon' },
  // ---- Primordial dialects (SRD stat-block convention) ----
  { id: 'Auran',        label: 'Auran' },
  { id: 'Aquan',        label: 'Aquan' },
  { id: 'Ignan',        label: 'Ignan' },
  { id: 'Terran',       label: 'Terran' },
  // ---- Secret + roster-attested ----
  { id: 'Druidic',      label: 'Druidic' },
  { id: 'Gnoll',        label: 'Gnoll' },
]);

// ============================================================================
// TOGGLE ATTRIBUTES — flexible attribute dropdown (UX-18, NOV 2025)
// ============================================================================
// [PURPOSE]: Every attribute a toggle effect (or passive effect) can modify.
//   Grouped by `type` so the renderer can use <optgroup> labels:
//     ability / combat / movement / skill / save / spellcasting / other
// [WHY]: Pre-UX-18 toggle effects were hardcoded to attackBonus / damageBonus /
//   acPenalty. Players asked for arbitrary attribute mods (speed buff toggle,
//   initiative toggle, etc.). This dropdown is the source of truth.
export const TOGGLE_ATTRIBUTES = {
  // Ability Scores
  str: { label: 'Strength', type: 'ability' },
  dex: { label: 'Dexterity', type: 'ability' },
  con: { label: 'Constitution', type: 'ability' },
  int: { label: 'Intelligence', type: 'ability' },
  wis: { label: 'Wisdom', type: 'ability' },
  cha: { label: 'Charisma', type: 'ability' },

  // Combat
  attackBonus: { label: 'Attack Rolls', type: 'combat' },
  damageBonus: { label: 'Damage', type: 'combat' },
  ac: { label: 'Armor Class', type: 'combat' },
  initiative: { label: 'Initiative', type: 'combat' },

  // Movement
  speed: { label: 'Walking Speed', type: 'movement' },
  climbSpeed: { label: 'Climb Speed', type: 'movement' },
  swimSpeed: { label: 'Swim Speed', type: 'movement' },
  flySpeed: { label: 'Fly Speed', type: 'movement' },

  // Skills (all 18 D&D 5e skills)
  acrobatics: { label: 'Acrobatics', type: 'skill' },
  animalHandling: { label: 'Animal Handling', type: 'skill' },
  arcana: { label: 'Arcana', type: 'skill' },
  athletics: { label: 'Athletics', type: 'skill' },
  deception: { label: 'Deception', type: 'skill' },
  history: { label: 'History', type: 'skill' },
  insight: { label: 'Insight', type: 'skill' },
  intimidation: { label: 'Intimidation', type: 'skill' },
  investigation: { label: 'Investigation', type: 'skill' },
  medicine: { label: 'Medicine', type: 'skill' },
  nature: { label: 'Nature', type: 'skill' },
  perception: { label: 'Perception', type: 'skill' },
  performance: { label: 'Performance', type: 'skill' },
  persuasion: { label: 'Persuasion', type: 'skill' },
  religion: { label: 'Religion', type: 'skill' },
  sleightOfHand: { label: 'Sleight of Hand', type: 'skill' },
  stealth: { label: 'Stealth', type: 'skill' },
  survival: { label: 'Survival', type: 'skill' },

  // Saving Throws
  strSave: { label: 'Strength Save', type: 'save' },
  dexSave: { label: 'Dexterity Save', type: 'save' },
  conSave: { label: 'Constitution Save', type: 'save' },
  intSave: { label: 'Intelligence Save', type: 'save' },
  wisSave: { label: 'Wisdom Save', type: 'save' },
  chaSave: { label: 'Charisma Save', type: 'save' },

  // Spellcasting
  spellSaveDC: { label: 'Spell Save DC', type: 'spellcasting' },
  spellAttackBonus: { label: 'Spell Attack Bonus', type: 'spellcasting' },

  // Other
  hp: { label: 'Hit Points', type: 'other' },
  proficiencyBonus: { label: 'Proficiency Bonus', type: 'other' },
  passivePerception: { label: 'Passive Perception', type: 'other' },
};
